// URL tracking utilities for preserving user journey context
// Captures landing URL, referrer, and UTM parameters on first visit

interface UrlTrackingData {
  landingUrl: string;
  landingPath: string;
  landingSearch: string;
  referrer: string;
  timestamp: number;
  queryParams: Record<string, string>;
}

const TRACKING_KEY = 'anemoi_landing_context';

/**
 * Initialize URL tracking on page load (call once on app start)
 * Captures the landing URL and referrer information
 */
export function initializeUrlTracking(): void {
  if (typeof window === 'undefined') return;

  // Only capture on first visit (don't overwrite existing data)
  const existing = sessionStorage.getItem(TRACKING_KEY);
  if (existing) return;

  const url = new URL(window.location.href);

  // Capture ALL query parameters generically
  const queryParams: Record<string, string> = {};
  url.searchParams.forEach((value, key) => {
    queryParams[key] = value;
  });

  const trackingData: UrlTrackingData = {
    landingUrl: window.location.href,
    landingPath: window.location.pathname,
    landingSearch: window.location.search,
    referrer: document.referrer || '',
    timestamp: Date.now(),
    queryParams,
  };

  sessionStorage.setItem(TRACKING_KEY, JSON.stringify(trackingData));
}

/**
 * Get the original landing URL context
 */
export function getLandingContext(): UrlTrackingData | null {
  if (typeof window === 'undefined') return null;

  const stored = sessionStorage.getItem(TRACKING_KEY);
  if (!stored) return null;

  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

/**
 * Get all query parameters from landing context
 */
export function getLandingQueryParams(): Record<string, string> {
  const landing = getLandingContext();
  return landing?.queryParams || {};
}
